import React, {useMemo} from 'react';
import DOMPurify from 'dompurify';

/**
 * 富文本安全展示组件（移动端，与 desktop-web 同款）。
 *
 * 用于渲染外部来源（如客服工单同步）的 HTML 富文本：含内联图片、排版的客诉正文等。
 * 必须经 DOMPurify 净化后再 dangerouslySetInnerHTML，剥离 script / 事件属性等，
 * 防止外部不可信 HTML 引入存储型 XSS。
 */
interface RichTextViewProps {
    /** 原始 HTML 字符串（可能来自外部系统，不可信） */
    html?: string | null;
    /** 容器额外 className */
    className?: string;
    /** 容器内联样式 */
    style?: React.CSSProperties;
}

const RichTextView: React.FC<RichTextViewProps> = ({html, className, style}) => {
    const safeHtml = useMemo(() => {
        if (!html) return '';
        // 默认配置已禁用 script / on* 事件等危险内容；保留 img / 排版标签用于展示
        return DOMPurify.sanitize(html, {USE_PROFILES: {html: true}});
    }, [html]);

    if (!safeHtml) return null;

    return (
        <div
            className={className}
            style={{
                // 强制超长无空格串（长 URL / email / 连续符号）在任意位置断行，防止富文本横向溢出屏幕；
                // overflowWrap:break-word 对连续长串常不生效，需配合 wordBreak:break-word
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                minWidth: 0,
                maxWidth: '100%',
                ...style,
            }}
            // 内容已经过 DOMPurify 净化，无 XSS 风险
            dangerouslySetInnerHTML={{__html: safeHtml}}
        />
    );
};

export default RichTextView;
